import {
    Component,
    ComponentFactoryResolver,
    Injector,
    ViewChild,
    ViewContainerRef,
} from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

import { AuthService } from '../../auth/auth.service';
import { StateToProps } from '../../store';
import { useDispatcher, UseStore } from '../../store.decorator';
import { User } from '../../user/user.model';
import { ScorecardDataService } from '../core/data.service';
import { deflate, inflate, InflateHandle } from '../core/template-inflater';
import { datasetKey } from '../scorecard.model';

import { HistoryPositionNavigate } from './general/history-position-navigate.model';
import { widgetTypeComponentMap } from './general/widget-type-component-map';
import { PWBridge, PWBridgeComm } from './pulse-widget-bridge.service';
import {
    listData,
    navigateHistory,
    reload,
    reset,
    selectPeriodType,
    setUser,
    toggleScorecardDetail,
    toggleYourTeamExpanded,
} from './pulse-widget.action';
import { TranslationKeyMap } from './pulse-widget.i18n';
import { scorecardWidgetReducer } from './pulse-widget.reducer';
import { ScorecardWidgetState } from './pulse-widget.state';

@Component({
    selector: 'cmps-scorecard-widget',
    templateUrl: './pulse-widget.component.html',
    host: {
        class: 'cmps-scorecard-widget',
    },
})
@UseStore<ScorecardWidgetState>(
    'scorecard.pulse-widget',
    scorecardWidgetReducer,
)
export class ScorecardWidgetComponent
    implements StateToProps<ScorecardWidgetState>, PWBridgeComm
{
    state: ScorecardWidgetState;

    @ViewChild('ref', { read: ViewContainerRef, static: true })
    private readonly _viewContainerRef: ViewContainerRef;

    private _inflateHdl: InflateHandle;

    /** @private */ _i18n = TranslationKeyMap;
    private readonly _yourTeamExpandedSubj: BehaviorSubject<boolean>;

    constructor(
        private readonly _injector: Injector,
        private readonly _factoryResolver: ComponentFactoryResolver,
        private readonly _authService: AuthService,
        private readonly _dataService: ScorecardDataService,
        private readonly _pwBridge: PWBridge,
    ) {
        this._yourTeamExpandedSubj = new BehaviorSubject(null);
    }

    ngOnInit(): void {
        this._pwBridge.connect(this);
    }

    ngOnDestroy(): void {
        // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
        this._yourTeamExpandedSubj && this._yourTeamExpandedSubj.complete();
        this._pwBridge.disconnect();
        deflate(this._inflateHdl);
    }

    stateToProps(prevState?: ScorecardWidgetState): void {
        this._reinflateCheck(prevState);

        this._reloadCheck(prevState);

        this._toggleYourTeamCheck(prevState);
    }

    propsToDispatch(): void {
        // TODO revise. this is temporary means of flushing tabs after auth user has changed
        if (
            this.state.authUserId != null &&
            this.state.authUserId !== this._authService.user.id
        ) {
            useDispatcher(this, reset({}));
        }

        this._loadUser(this._authService.user.copy(), true);
    }

    bridgeYourTeamExpandedChange(): Observable<boolean> {
        return this._yourTeamExpandedSubj.asObservable();
    }

    /** @private */
    _onToggleScorecardDetail(): void {
        useDispatcher(this, toggleScorecardDetail({}));
    }

    /** @private */
    _onExpandYourTeamClick(): void {
        useDispatcher(this, toggleYourTeamExpanded({}));
    }

    /** @private */
    _onUserSelect(user: User): void {
        this._loadUser(user, false);
    }

    /** @private */
    _onNavigate(historyPosNav: HistoryPositionNavigate): void {
        useDispatcher(this, navigateHistory({ historyPosNav }));
    }

    /** @private */
    _onPeriodTypeFocus(): void {
        const loaded = Object.keys(this.state.scorecardHistory);
        const toLoad = this.state.periodTypes
            // eslint-disable-next-line @typescript-eslint/prefer-includes
            .filter((e) => loaded.indexOf(e.periodTypeId.toString()) === -1)
            .map((e) => e.periodTypeId);
        if (!toLoad.length) {
            return;
        }
        useDispatcher(this, reload({ periodTypes: toLoad }));
    }

    /** @private */
    _onPeriodTypeChange(periodType: string): void {
        useDispatcher(
            this,
            selectPeriodType({ periodType: parseInt(periodType) }),
        );
    }

    private _loadUser(user: User, isOnStateConnect: boolean): void {
        useDispatcher(
            this,
            setUser({
                user,
                authUserId: this._authService.user.id,
                isOnStateConnect,
            }),
        );
        useDispatcher(this, reload({}));
    }

    // TODO consolidate with main scorecard component
    private _reinflateCheck(prevState: ScorecardWidgetState): void {
        const prevSc = prevState ? prevState.selectedScorecard : null;
        const sc = this.state ? this.state.selectedScorecard : null;

        const deInflate =
            !prevSc ||
            !prevSc.data ||
            !prevSc.data.widgetTemplateConfig ||
            !sc ||
            !sc.data ||
            !sc.data.widgetTemplateConfig ||
            prevSc.data.widgetTemplateConfig.identifier !==
                sc.data.widgetTemplateConfig.identifier;
        const reinflate =
            deInflate && sc && sc.data && sc.data.scorecardId != null;

        if (deInflate) {
            deflate(this._inflateHdl);
        }

        // if no selected scorecard then all widgets would already be deflated
        sc && this._dataService.changeScorecardData(sc);

        if (reinflate) {
            inflate(
                this._injector,
                this._factoryResolver,
                this._viewContainerRef,
                widgetTypeComponentMap,
                { paddingRight: '0.5rem', marginBottom: '0.5rem' },
                sc.data.widgetTemplateConfig,
                () => false,
                (hdl) => {
                    this._inflateHdl = hdl;
                },
            );
        }
    }

    // TODO consolidate with main scorecard component
    private _reloadCheck(prevState: ScorecardWidgetState): void {
        if (
            prevState.pending.reload &&
            !this.state.pending.reload &&
            this.state.scorecardHistory &&
            this.state.selectedPeriodType
        ) {
            const loaded = Object.keys(this.state.scorecardDataset);
            const scorecardIds: number[] = [];

            // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
            this.state.periodTypes &&
                this.state.periodTypes.forEach((periodType) => {
                    const dat =
                        this.state.scorecardHistory[periodType.periodTypeId];
                    if (!dat) {
                        return;
                    }
                    const ids = dat
                        .map((e) => e.scorecardId)
                        // eslint-disable-next-line @typescript-eslint/prefer-includes
                        .filter(
                            (e) =>
                                // eslint-disable-next-line @typescript-eslint/prefer-includes
                                loaded.indexOf(
                                    datasetKey(periodType.periodTypeId, e),
                                ) === -1,
                        );
                    scorecardIds.push(...ids);
                });

            useDispatcher(this, listData({ scorecardIds }));
        }
    }

    private _toggleYourTeamCheck(prevState: ScorecardWidgetState): void {
        if (prevState.yourTeamExpanded !== this.state.yourTeamExpanded) {
            this._yourTeamExpandedSubj.next(this.state.yourTeamExpanded);
        }
    }
}
